/**
 * Durable soul-proposal decision card (approved / dismissed) in the Inbox reader timeline.
 */
import { CheckCircle2, FileText, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

export type SoulProposalDecision = 'approved' | 'dismissed'

export type SoulProposalDecisionEventProps = {
  decision: SoulProposalDecision
  actorLabel: string
  at: string
  documentId: string
  filename: string
  targetPath: string
  /** Markdown body already includes the link; we still render a clear open control. */
  onOpenDocument: (documentId: string) => void
  className?: string
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function SoulProposalDecisionEvent({
  decision,
  actorLabel,
  at,
  documentId,
  filename,
  targetPath,
  onOpenDocument,
  className,
}: SoulProposalDecisionEventProps) {
  const approved = decision === 'approved'
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-3',
        approved
          ? 'border-green-500/30 bg-green-500/10'
          : 'border-muted-foreground/25 bg-muted/40',
        className,
      )}
      data-testid={testIds.inbox.soulProposalDecisionEvent}
      data-decision={decision}
    >
      <div className="flex items-start gap-2.5">
        {approved ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
        ) : (
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className={cn(
                'text-sm font-semibold',
                approved ? 'text-green-800 dark:text-green-200' : 'text-foreground',
              )}
            >
              {approved ? 'Approved' : 'Dismissed'}
            </span>
            <span className="text-xs text-muted-foreground">
              by {actorLabel} · {formatWhen(at)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {approved
              ? 'Patch applied to the soul document.'
              : 'No changes written to the soul document.'}
          </p>
          <button
            type="button"
            onClick={() => {
              const target = (documentId || targetPath || '').trim()
              if (!target) return
              onOpenDocument(target)
            }}
            disabled={!(documentId || targetPath)?.trim()}
            className="inline-flex max-w-full items-center gap-1.5 rounded border border-border bg-background/80 px-2 py-1 text-left text-xs font-medium text-primary hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
            data-testid={testIds.inbox.soulProposalDecisionDocLink}
            title={targetPath}
          >
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{filename}</span>
          </button>
          <p className="truncate font-mono text-[10px] text-muted-foreground/80" title={targetPath}>
            {targetPath}
          </p>
        </div>
      </div>
    </div>
  )
}
